import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { TrendingUp, Users, ShoppingBag, DollarSign, RefreshCw, Check } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACK_NAMES = {
  basic_math: 'Basic Math',
  advanced_ops: 'Advanced Ops',
  ultimate_math: 'Ultimate',
  dark_mode_skin: 'Dark Mode',
  sound_effects: 'Sound Effects',
  history_log: 'History Log',
  bundle_all: 'Bundle All',
};

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/admin/stats`, { credentials: 'include' });
      if (res.status === 401) { navigate('/'); return; }
      if (res.status === 403) { setError('Admin access required. Make sure you are logged in as admin@itorzo.com'); return; }
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      setStats(data);
      setLastRefresh(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const [grantEmail, setGrantEmail] = useState('');
  const [grantPacks, setGrantPacks] = useState('');
  const [grantAmount, setGrantAmount] = useState('');
  const [grantResult, setGrantResult] = useState(null);
  const [granting, setGranting] = useState(false);

  const handleGrant = useCallback(async () => {
    if (!grantEmail.trim() || !grantPacks.trim()) return;
    setGranting(true);
    setGrantResult(null);
    try {
      const pack_ids = grantPacks.split(',').map(p => p.trim()).filter(Boolean);
      const res = await fetch(`${API}/admin/grant-unlock`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: grantEmail.trim(),
          pack_ids,
          amount: parseFloat(grantAmount) || 0,
          note: 'admin-manual-grant',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setGrantResult({ ok: true, msg: `Granted [${data.granted.join(', ')}] to ${data.user}`, packs: data.unlocked_packs });
      setGrantEmail(''); setGrantPacks(''); setGrantAmount('');
      fetchStats();
    } catch (e) {
      setGrantResult({ ok: false, msg: e.message });
    } finally {
      setGranting(false);
    }
  }, [grantEmail, grantPacks, grantAmount, fetchStats]);

  const fmt = (n) => n?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-950">
      <Helmet>
        <title>Admin Dashboard — CalcByEA™</title>
        <meta name="description" content="CalcByEA admin dashboard. Revenue analytics, transaction history, and pack management." />
        <link rel="canonical" href="https://calculatorbyea.com/admin" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Navbar />
      <div className="pt-20 max-w-5xl mx-auto px-6 pb-16">

        {/* Header */}
        <div className="flex items-end justify-between mb-10 border-b-2 border-zinc-200 pb-6">
          <div>
            <div className="text-[9px] font-bold uppercase tracking-[0.35em] text-zinc-400 mb-2">Admin</div>
            <h1 className="font-bebas text-5xl text-zinc-950 leading-none">Dashboard</h1>
            {lastRefresh && <p className="text-xs text-zinc-400 mt-1">Updated {fmtTime(lastRefresh.toISOString())}</p>}
          </div>
          <button
            onClick={fetchStats}
            disabled={loading}
            className="flex items-center gap-2 border-2 border-zinc-300 text-zinc-500 hover:border-zinc-950 hover:text-zinc-950 text-xs font-bold uppercase tracking-widest px-4 py-2.5 transition-all duration-100 disabled:opacity-40"
            data-testid="admin-refresh-btn"
          >
            <RefreshCw size={12} strokeWidth={2.5} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {error && (
          <div className="border-2 border-zinc-200 bg-white p-4 mb-8 text-sm text-zinc-600" data-testid="admin-error">
            {error}
          </div>
        )}

        {loading && !stats && (
          <div className="text-center py-20">
            <div className="font-bebas text-3xl text-zinc-300 animate-pulse">Loading Premium Analytics™</div>
          </div>
        )}

        {stats && (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-2 border-zinc-950 mb-8 shadow-[6px_6px_0px_0px_rgba(9,9,11,1)]">
              {[
                { label: 'Total Revenue', value: `$${fmt(stats.total_revenue)}`, icon: <DollarSign size={14} strokeWidth={2.5} />, dark: true },
                { label: 'Transactions', value: stats.total_transactions.toLocaleString(), icon: <TrendingUp size={14} strokeWidth={2.5} />, dark: false },
                { label: 'Total Users', value: stats.total_users.toLocaleString(), icon: <Users size={14} strokeWidth={2.5} />, dark: false },
                { label: 'Paying Users', value: stats.paying_users.toLocaleString(), icon: <ShoppingBag size={14} strokeWidth={2.5} />, dark: true },
              ].map((kpi, i) => (
                <div
                  key={kpi.label}
                  className={`p-6 border-r-2 last:border-r-0 border-zinc-950 ${kpi.dark ? 'bg-zinc-950 text-white' : 'bg-white text-zinc-950'} ${i >= 2 ? 'border-t-2 md:border-t-0' : ''}`}
                  data-testid={`kpi-${kpi.label.toLowerCase().replace(/ /g,'-')}`}
                >
                  <div className={`mb-3 ${kpi.dark ? 'text-zinc-500' : 'text-zinc-400'}`}>{kpi.icon}</div>
                  <div className={`font-bebas text-4xl leading-none mb-1 ${kpi.dark ? 'text-white' : 'text-zinc-950'}`}>{kpi.value}</div>
                  <div className={`text-[9px] font-bold uppercase tracking-[0.25em] ${kpi.dark ? 'text-zinc-500' : 'text-zinc-400'}`}>{kpi.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              {/* Pack Breakdown */}
              <div className="border-2 border-zinc-200 bg-white" data-testid="pack-breakdown">
                <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
                  <h2 className="font-bebas text-xl text-zinc-950">Pack Sales</h2>
                  <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-400">{stats.pack_breakdown.length} packs sold</span>
                </div>
                {stats.pack_breakdown.length === 0 ? (
                  <div className="px-5 py-8 text-center text-xs text-zinc-400 font-bold uppercase tracking-widest">No sales yet</div>
                ) : (
                  <div className="divide-y divide-zinc-100">
                    {stats.pack_breakdown
                      .sort((a, b) => b.revenue - a.revenue)
                      .map(pack => {
                        const maxRevenue = Math.max(...stats.pack_breakdown.map(p => p.revenue));
                        const pct = maxRevenue > 0 ? (pack.revenue / maxRevenue) * 100 : 0;
                        return (
                          <div key={pack.pack_id} className="px-5 py-3.5">
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-sm font-semibold text-zinc-950">{PACK_NAMES[pack.pack_id] || pack.pack_id}</span>
                              <div className="flex items-center gap-3">
                                <span className="text-xs text-zinc-400">{pack.count}x</span>
                                <span className="font-bebas text-lg text-zinc-950">${fmt(pack.revenue)}</span>
                              </div>
                            </div>
                            <div className="h-1 bg-zinc-100 w-full">
                              <div className="h-1 bg-zinc-950 transition-all duration-500" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>

              {/* Recent Transactions */}
              <div className="border-2 border-zinc-200 bg-white" data-testid="recent-transactions">
                <div className="px-5 py-4 border-b border-zinc-100">
                  <h2 className="font-bebas text-xl text-zinc-950">Recent Transactions</h2>
                </div>
                {stats.recent_transactions.length === 0 ? (
                  <div className="px-5 py-8 text-center text-xs text-zinc-400 font-bold uppercase tracking-widest">No transactions yet</div>
                ) : (
                  <div className="divide-y divide-zinc-100 max-h-80 overflow-y-auto">
                    {stats.recent_transactions.map((tx, i) => (
                      <div key={i} className="px-5 py-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-xs font-semibold text-zinc-950 truncate">{PACK_NAMES[tx.pack_id] || tx.pack_id}</div>
                          <div className="text-[10px] text-zinc-400 truncate font-mono">{tx.user_id?.slice(0, 16)}...</div>
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="font-bebas text-lg text-zinc-950">${fmt(tx.amount)}</div>
                          <div className="text-[9px] text-zinc-400">{fmtTime(tx.created_at)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Conversion Rate */}
            {stats.total_users > 0 && (
              <div className="mt-6 border-2 border-zinc-200 bg-white px-6 py-5 flex items-center justify-between">
                <div>
                  <div className="text-[9px] font-bold uppercase tracking-[0.3em] text-zinc-400 mb-1">Conversion Rate</div>
                  <div className="font-bebas text-3xl text-zinc-950">
                    {((stats.paying_users / stats.total_users) * 100).toFixed(1)}%
                  </div>
                  <div className="text-xs text-zinc-400 mt-0.5">{stats.paying_users} of {stats.total_users} users purchased</div>
                </div>
                <div className="w-24 h-24 relative flex items-center justify-center">
                  <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="#E4E4E7" strokeWidth="2" />
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="#09090B" strokeWidth="2"
                      strokeDasharray={`${(stats.paying_users / stats.total_users) * 100} 100`}
                      strokeLinecap="square" />
                  </svg>
                </div>
              </div>
            )}

            {/* Manual Unlock Grant */}
            <div className="mt-6 border-2 border-zinc-200 bg-white" data-testid="grant-unlock-panel">
              <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
                <h2 className="font-bebas text-xl text-zinc-950">Manual Unlock Grant</h2>
                <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-400">Fix missed payments</span>
              </div>
              <div className="px-5 py-4 space-y-3">
                <p className="text-xs text-zinc-400">Use this to manually grant packs to users whose Stripe payment succeeded but unlocks were not applied.</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <input
                    type="email"
                    value={grantEmail}
                    onChange={e => setGrantEmail(e.target.value)}
                    placeholder="user@email.com"
                    className="border-2 border-zinc-200 px-3 py-2 text-sm text-zinc-950 placeholder:text-zinc-300 focus:border-zinc-950 outline-none transition-colors"
                    data-testid="grant-email-input"
                  />
                  <input
                    type="text"
                    value={grantPacks}
                    onChange={e => setGrantPacks(e.target.value)}
                    placeholder="bundle_all or basic_math, advanced_ops"
                    className="border-2 border-zinc-200 px-3 py-2 text-sm text-zinc-950 placeholder:text-zinc-300 focus:border-zinc-950 outline-none transition-colors"
                    data-testid="grant-packs-input"
                  />
                  <input
                    type="number"
                    value={grantAmount}
                    onChange={e => setGrantAmount(e.target.value)}
                    placeholder="Amount paid (e.g. 13.97)"
                    className="border-2 border-zinc-200 px-3 py-2 text-sm text-zinc-950 placeholder:text-zinc-300 focus:border-zinc-950 outline-none transition-colors"
                    data-testid="grant-amount-input"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleGrant}
                    disabled={granting || !grantEmail.trim() || !grantPacks.trim()}
                    className="flex items-center gap-2 bg-zinc-950 text-white font-bold uppercase tracking-widest text-xs px-5 py-2.5 border-2 border-zinc-950 shadow-[3px_3px_0px_0px_rgba(9,9,11,1)] hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_0px_rgba(9,9,11,1)] disabled:opacity-40 transition-all duration-100"
                    data-testid="grant-submit-btn"
                  >
                    {granting ? <RefreshCw size={11} className="animate-spin" /> : <Check size={11} strokeWidth={3} />}
                    Grant Unlock
                  </button>
                  <div className="text-[10px] text-zinc-400 font-mono">
                    Pack IDs: basic_math · advanced_ops · ultimate_math · dark_mode_skin · sound_effects · history_log · bundle_all
                  </div>
                </div>
                {grantResult && (
                  <div
                    className={`text-xs px-3 py-2 border-2 font-semibold ${grantResult.ok ? 'border-green-200 bg-green-50 text-green-700' : 'border-red-200 bg-red-50 text-red-700'}`}
                    data-testid="grant-result"
                  >
                    {grantResult.msg}
                    {grantResult.ok && grantResult.packs && (
                      <span className="ml-2 text-zinc-400 font-normal">All unlocks: [{grantResult.packs.join(', ')}]</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
